import { useCallback, useMemo, useState } from 'react';

import {
  dashboardUserStatus,
  IDashboardUsersFiltersForm,
  ISupportUser,
  T2FaStatus,
  TDashboardUserStatus,
} from '@/features/dashboard-users/store/support-user.model';
import { useAppDispatch } from '@/hooks/redux-helpers';
import { useCurrentSupportUser } from '@/features/dashboard-users/hooks/useCurrentSupportUser';
import { useCursor } from '@/hooks/useCursor';
import { PER_PAGE_USERS } from '@/features/dashboard-users/components/DashboardUsers';
import { advancedRoleStatus, TAdvancedRoleSlug } from '@/features/roles/store/models/advancedRole.model';
import { useDashboardUserTableHeaders } from '@/features/dashboard-users/hooks/table/useDashboardUserTableHeaders';
import { useDashboardUserTableData } from '@/features/dashboard-users/hooks/table/useDashboardUserTableData';
import { openModal } from '@/store/actions/core.actions';
import { useAccesControl } from '@/hooks/useAccessControl';
import { getSupportUserScopes } from '@/features/roles/helpers/getSupportUserScopes';
import { EScope } from '@/features/roles/store/models/role.model';
import useDashboradUsersFilters from '@/features/dashboard-users/hooks/table/useDashboradUsersFilters';
import { useSuspendedDashboardUsers } from '@/features/dashboard-users/hooks/useSuspendedDashboardUsers';
import { useActiveDashboardUsers } from '@/features/dashboard-users/hooks/useActiveDashboardUsers';

const defaultFilters: IDashboardUsersFiltersForm = {
  username: '',
  role: null,
  status: null,
  '2faStatus': null,
};

export function useDashboardUsersTable(onManageDashboardUser: (dashboardUser: ISupportUser) => void) {
  const dispatch = useAppDispatch();
  const { id: currentSupportUserId } = useCurrentSupportUser();

  const {
    activeDashboardUsers,
    activeDashboardUsersCursor,
    isActiveDashboardUsersLoading,
    handleNavigationActiveDashboardUsers,
  } = useActiveDashboardUsers();

  const {
    suspendedDashboardUsers,
    suspendedDashboardUsersCursor,
    isSuspendedDashboardUsersLoading,
    handleNavigationSuspendedDashboardUsers,
  } = useSuspendedDashboardUsers();

  const [isActiveDashboardUserDisplayed, setIsActiveDashboardUserDisplayed] = useState(true);
  const [filters, setFilters] = useState<IDashboardUsersFiltersForm>(defaultFilters);

  const dashboardUsers = useMemo(
    () => (isActiveDashboardUserDisplayed ? activeDashboardUsers : suspendedDashboardUsers),
    [isActiveDashboardUserDisplayed, activeDashboardUsers, suspendedDashboardUsers],
  );

  const cursor = useMemo(
    () => (isActiveDashboardUserDisplayed ? activeDashboardUsersCursor : suspendedDashboardUsersCursor),
    [activeDashboardUsersCursor, isActiveDashboardUserDisplayed, suspendedDashboardUsersCursor],
  );

  const isLoading = useMemo(
    () => (isActiveDashboardUserDisplayed ? isActiveDashboardUsersLoading : isSuspendedDashboardUsersLoading),
    [isActiveDashboardUserDisplayed, isActiveDashboardUsersLoading, isSuspendedDashboardUsersLoading],
  );

  const { advandeRoleFromScopes, advandeRoleFromSlug } = useAccesControl();

  const filterByUsername = useCallback(
    (dashboardUsers: ISupportUser[], username: string) =>
      dashboardUsers?.filter((user) => user.username.toLowerCase().includes(username.toLowerCase())),
    [],
  );

  const filterByRole = useCallback(
    (dashboardUsers: ISupportUser[], role: TAdvancedRoleSlug) =>
      dashboardUsers?.filter((dashboardUser) => {
        const scopes = getSupportUserScopes(dashboardUser);
        const uniqueScopes: EScope[] = [...new Set(scopes)] as EScope[];
        const dashboardUserRole =
          dashboardUser.metadata?.role?.status === advancedRoleStatus.VALIDATED
            ? advandeRoleFromSlug(dashboardUser.metadata.role.slug)
            : advandeRoleFromScopes(uniqueScopes);
        return dashboardUserRole.slug === role;
      }),
    [advandeRoleFromScopes, advandeRoleFromSlug],
  );

  const filterByStatus = useCallback(
    (dashboardUsers: ISupportUser[], status: TDashboardUserStatus) =>
      dashboardUsers?.filter(({ accessExpirationDate, wrongPwdAttempts }) => {
        const isBlocked = wrongPwdAttempts >= 3;
        const last = accessExpirationDate ? new Date(accessExpirationDate)?.getTime() - Date.now() : undefined;

        if (status === dashboardUserStatus.ACTIVE) {
          return !isBlocked && (!accessExpirationDate || (last !== undefined && last > 2592000000));
        }
        if (status === dashboardUserStatus.BLOCKED) {
          return isBlocked;
        }
        if (status === dashboardUserStatus.EXPIRED) {
          return last !== undefined && last <= 0;
        }
        if (status === dashboardUserStatus.NEAR_EXPIRED) {
          return last !== undefined && last <= 2592000000 && last > 0;
        }
        return false;
      }),
    [],
  );

  const filterBy2FaStatus = useCallback(
    (dashboardUsers: ISupportUser[], twoFaStatus: T2FaStatus) =>
      dashboardUsers?.filter((dashboardUser) =>
        twoFaStatus === 'enabled' ? !!dashboardUser.secureKey : !dashboardUser.secureKey,
      ),
    [],
  );

  const filteredDashboardUsers = useMemo(() => {
    const { username, role, status, '2faStatus': twoFaStatus } = filters;
    let _filteredDashboardUsers = dashboardUsers;

    if (!!username) _filteredDashboardUsers = filterByUsername(_filteredDashboardUsers, username);
    if (!!role) _filteredDashboardUsers = filterByRole(_filteredDashboardUsers, role);
    if (!!status) _filteredDashboardUsers = filterByStatus(_filteredDashboardUsers, status);
    if (!!twoFaStatus) _filteredDashboardUsers = filterBy2FaStatus(_filteredDashboardUsers, twoFaStatus);

    return _filteredDashboardUsers;
  }, [dashboardUsers, filters, filterByUsername, filterByRole, filterByStatus, filterBy2FaStatus]);

  const shouldDisplayReviewNotice =
    isActiveDashboardUserDisplayed &&
    !activeDashboardUsers.every(
      (dashboardUser) => dashboardUser?.metadata?.role?.status === advancedRoleStatus.VALIDATED,
    );

  const columns = useDashboardUserTableHeaders(isActiveDashboardUserDisplayed);

  const rows = useDashboardUserTableData(
    filteredDashboardUsers,
    shouldDisplayReviewNotice,
    currentSupportUserId,
    isLoading,
    isActiveDashboardUserDisplayed,
    onManageDashboardUser,
  );

  const handleCompleteReview = useCallback(() => {
    dispatch(openModal('supportUserReviewModal'));
  }, [dispatch]);

  const handleFilterChange = useCallback((newFilters: IDashboardUsersFiltersForm) => {
    setFilters(newFilters || defaultFilters);
  }, []);

  const { usernameField, userStatusField, userRoleField, twoFaField, clearFilters, hasFilters } =
    useDashboradUsersFilters({
      onChange: handleFilterChange,
      initialValues: defaultFilters,
    });

  const handleNavigation = useCallback(
    (cursorId: string) => {
      if (isActiveDashboardUserDisplayed) {
        handleNavigationActiveDashboardUsers(cursorId);
      } else {
        handleNavigationSuspendedDashboardUsers(cursorId);
      }
      if (hasFilters) {
        clearFilters();
      }
    },
    [
      isActiveDashboardUserDisplayed,
      handleNavigationActiveDashboardUsers,
      handleNavigationSuspendedDashboardUsers,
      hasFilters,
      clearFilters,
    ],
  );

  const pagination = useCursor(cursor, PER_PAGE_USERS, handleNavigation);

  const onNavigateToSuspendedDashboardUser = useCallback(() => {
    setIsActiveDashboardUserDisplayed(false);
    clearFilters();
  }, [clearFilters]);

  const onNavigateToActiveDashboardUser = useCallback(() => {
    setIsActiveDashboardUserDisplayed(true);
    clearFilters();
  }, [clearFilters]);

  return {
    cursor,
    rows,
    columns,
    pagination,
    shouldDisplayReviewNotice,
    handleCompleteReview,
    hasFilters,
    clearFilters,
    isActiveDashboardUserDisplayed,
    onNavigateToSuspendedDashboardUser,
    onNavigateToActiveDashboardUser,
    fields: {
      username: usernameField,
      userStatus: userStatusField,
      userRole: userRoleField,
      twoFa: twoFaField,
    },
    suspendedDashboardUsers,
  };
}
